import React, { useEffect, useRef } from 'react';
import isAfter from 'date-fns/isAfter';
import isBefore from 'date-fns/isBefore';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import { componentWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/component-with-condition/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { lazyAfterPaint, lazy } from '@atlassian/react-loosely-lazy';
import { NOT_LOADED, NO_VALUE, useProperties } from './useProperties.tsx';

const LazyJsmCustomDomainChangeBoardingFlag = lazyAfterPaint(
	() =>
		import(
			/* webpackChunkName: "async-jsm-custom-domain-change-boarding-flag" */ './JsmCustomDomainChangeBoardingFlag.tsx'
		),
	{ entryFsSsrLiftImportToModule: true },
);

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const LazyJsmCustomDomainChangeBoardingFlagNew = lazy(
	() =>
		import(
			/* webpackChunkName: "async-jsm-custom-domain-change-boarding-flag-new" */ './JsmCustomDomainChangeBoardingFlag.tsx'
		).then((exportedModule) => exportedModule.JsmCustomDomainChangeBoardingFlagNew),
	{ entryFsSsrLiftImportToModule: true },
);

const MILLISECONDS_IN_A_DAY: number = 24 * 60 * 1000;

const AsyncJsmCustomDomainChangeBoardingFlagOld = (): React.JSX.Element => {
	return (
		<JSErrorBoundary
			id="async-jsm-custom-domain-change-boarding-flag"
			packageName="jira-help-center-custom-domain-change-boarding"
		>
			<Placeholder name="jsm-custom-domain-change-boarding-flag" fallback={null}>
				<LazyJsmCustomDomainChangeBoardingFlag />
			</Placeholder>
		</JSErrorBoundary>
	);
};

const AsyncJsmCustomDomainChangeBoardingFlagNew = (): React.JSX.Element | null => {
	// oxlint-disable-next-line react/react-compiler
	const currentTime = Date.now();
	const [
		{ userSeenFlagDate, showFlagUpdateStartDate, helpCenterUrls, customDomainStatus },
		{ updateUserProperty, fetchApplicationProperty, fetchSeenFlagDateProperty },
	] = useProperties();
	const fetchPropertiesRef = useRef<() => Promise<unknown[]>>(() => Promise.resolve([]));

	useEffect(() => {
		fetchPropertiesRef.current = () =>
			Promise.all([fetchApplicationProperty(), fetchSeenFlagDateProperty()]);
	}, [fetchApplicationProperty, fetchSeenFlagDateProperty]);

	useEffect(() => {
		const fetchProperties = () => {
			Promise.resolve(fetchPropertiesRef.current()).catch(() => undefined);
		};
		fetchProperties();
		const pollInterval = setInterval(fetchProperties, MILLISECONDS_IN_A_DAY);
		return () => clearInterval(pollInterval);
	}, []);

	if (helpCenterUrls === undefined || customDomainStatus === undefined) {
		return null;
	}
	if (userSeenFlagDate === NOT_LOADED || showFlagUpdateStartDate === NOT_LOADED) {
		return null;
	}
	if (showFlagUpdateStartDate === NO_VALUE) {
		return null;
	}
	if (isBefore(new Date(currentTime), new Date(showFlagUpdateStartDate))) {
		return null;
	}
	if (
		userSeenFlagDate !== NO_VALUE &&
		isAfter(new Date(userSeenFlagDate), new Date(showFlagUpdateStartDate))
	) {
		return null;
	}

	return (
		<JSErrorBoundary
			id="async-jsm-custom-domain-change-boarding-flag"
			packageName="jira-help-center-custom-domain-change-boarding"
		>
			<Placeholder name="jsm-custom-domain-change-boarding-flag" fallback={null}>
				<LazyJsmCustomDomainChangeBoardingFlagNew
					helpCenterUrls={helpCenterUrls}
					customDomainStatus={customDomainStatus}
					onUpdateUserProperty={updateUserProperty}
				/>
			</Placeholder>
		</JSErrorBoundary>
	);
};

export const AsyncJsmCustomDomainChangeBoardingFlag: React.ComponentType = componentWithCondition(
	() => fg('ttai_fix_jsm_custom_domain_change_boarding_flag'),
	AsyncJsmCustomDomainChangeBoardingFlagNew,
	AsyncJsmCustomDomainChangeBoardingFlagOld,
);
